'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { useDensity } from '@liftango/ui-tokens';

export interface TextInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'prefix'
> {
  value: string;
  onChange: (value: string) => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  error?: boolean;
}

const Wrapper = styled.div<{ $error?: boolean; $disabled?: boolean; $height: number }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.xs};
  height: ${(p) => p.$height}px;
  border: 1px solid
    ${(p) => (p.$error ? p.theme.colors.semantic.error.color : p.theme.colors.neutral.line)};
  border-radius: ${(p) => p.theme.radius.md};
  padding: 0 ${(p) => p.theme.spaceAlias.sm};
  background: ${(p) => p.theme.colors.neutral.panel};
  ${(p) =>
    p.$disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
  &:focus-within {
    border-color: ${(p) => p.theme.colors.brand.primary};
    outline: ${(p) => p.theme.focus.width} solid ${(p) => p.theme.colors.brand.primary};
    outline-offset: ${(p) => p.theme.focus.insetOffset};
  }
`;

const Input = styled.input`
  border: none;
  background: transparent;
  outline: none;
  flex: 1;
  min-width: 0;
  padding: 0;
  font-size: ${(p) => p.theme.fontSize.body};
  font-family: ${(p) => p.theme.fontFamily.ui};
  color: ${(p) => p.theme.colors.neutral.ink};
  &::placeholder {
    color: ${(p) => p.theme.colors.neutral.inkFaint};
  }
`;

const Affix = styled.span`
  display: inline-flex;
  color: ${(p) => p.theme.colors.neutral.inkMute};
  flex-shrink: 0;
`;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { value, onChange, prefix, suffix, error, disabled, ...rest },
  ref,
) {
  const density = useDensity();
  return (
    <Wrapper $error={error} $disabled={disabled} $height={density.controlHeight}>
      {prefix && <Affix>{prefix}</Affix>}
      <Input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        {...rest}
      />
      {suffix && <Affix>{suffix}</Affix>}
    </Wrapper>
  );
});
